import { normalizeApproximateCostPlan } from '../api/approximateCostPlan';
import type { DavisBaconScheduleJob, EstimateLoadData } from '../api/constructionPriceEstimatorFunctions';

const ACTIVE_JOB_STATUSES = new Set(['pending', 'queued', 'processing', 'running']);

export const davisBaconCalculationFailureMessage = (job: DavisBaconScheduleJob): string => {
  if (job.status === 'stale') {
    return 'The Wage Determination lookup became stale after the project details changed. Save the current details and calculate prices again.';
  }
  if (job.status === 'cancelled') {
    return 'The Wage Determination lookup was cancelled before prices could be prepared.';
  }
  if (job.failure_reason === 'no_matching_determination') {
    return 'No applicable Wage Determination was found for the saved location and construction type.';
  }
  if (job.failure_reason === 'source_unavailable') {
    return 'Wage Determination source data is temporarily unavailable.';
  }
  return 'Wage Determination rates could not be read for this calculation.';
};

export const isFailedDavisBaconCalculation = (job: DavisBaconScheduleJob | null | undefined): job is DavisBaconScheduleJob => Boolean(
  job && job.saved_schedule_reused !== true && ['failed', 'stale', 'cancelled'].includes(job.status),
);

/**
 * A Calculate result is durable only after its persisted revision and its
 * saved schedule agree that usable wage rows were applied.  The schedule-job
 * status is a separate, eventually-consistent projection, so it can briefly
 * expose an older terminal failure after this positive result is published.
 */
export const hasTerminalWageBackedRevision = (value: EstimateLoadData): boolean => {
  const parentIsTerminal = value.job?.status === 'complete' || value.job?.status === 'completed';
  const schedule = value.davis_bacon_schedule;
  const plan = normalizeApproximateCostPlan(
    value.active_revision?.approximate_cost_plan
      ?? value.active_revision?.unified_source_research?.approximate_cost_plan,
  );
  return Boolean(
    parentIsTerminal
    && schedule
    && schedule.status !== 'reference_found'
    && schedule.classification_count > 0
    && plan
    && plan.labor_total !== null
    && plan.labor_lines.length > 0
    && (plan.labor_coverage?.status === 'complete' || plan.labor_coverage?.status === 'partial'),
  );
};
